import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { OpenXAgentPanelSection } from '../../shared/OpenXAgentPanelSection.tsx';
import type { OpenXAgentMatchedSegmentPricing } from '../utils/types.ts';
import {
  feeSummaryListSx,
  feeSummarySx,
  pricingTableContainerSx,
  pricingTableSx,
} from './MatchedSegmentPricing.styles.ts';

type MatchedSegmentPricingProps = {
  pricing: OpenXAgentMatchedSegmentPricing;
};

export const MatchedSegmentPricing = ({ pricing }: MatchedSegmentPricingProps) => {
  const { t } = useTranslation();
  const { partners, averagePercentOfMedia, averageFixedCpm } = pricing;

  return (
    <OpenXAgentPanelSection title={t('Data Partner Pricing')}>
      <Typography variant="body1" sx={{ mt: 0.5, color: 'text.secondary' }}>
        {t("You've selected segments from 3P Data Partners — pricing details are outlined below.")}
      </Typography>

      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={pricingTableContainerSx}>
          <Table
            aria-label={t('Data partner pricing')}
            data-test="openx-agent-matched-segment-pricing-table"
            sx={pricingTableSx}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('Partner Name')}</TableCell>
                <TableCell align="right">{t('% of Media')}</TableCell>
                <TableCell align="right">{t('Fixed CPM')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {partners.map(({ id, name, percentOfMedia, fixedCpm }) => (
                <TableRow key={id} data-test={`openx-agent-matched-segment-pricing-row-${id}`}>
                  <TableCell>{name}</TableCell>
                  <TableCell align="right">{percentOfMedia}</TableCell>
                  <TableCell align="right">{fixedCpm}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box data-test="openx-agent-matched-segment-fee-summary" sx={feeSummarySx}>
          <Typography variant="h3" component="h4" sx={{ m: 0, color: 'text.primary' }}>
            {t('Fee Summary')}
          </Typography>

          <Box component="dl" sx={feeSummaryListSx}>
            <Typography variant="caption" component="dt" sx={{ color: 'text.secondary' }}>
              {t('Average Percent of Media')}
            </Typography>

            <Typography variant="body1Bold" component="dd" sx={{ m: 0 }}>
              {averagePercentOfMedia}
            </Typography>

            <Typography variant="caption" component="dt" sx={{ color: 'text.secondary' }}>
              {t('Average Fixed Data CPM')}
            </Typography>

            <Typography variant="body1Bold" component="dd" sx={{ m: 0 }}>
              {averageFixedCpm}
            </Typography>
          </Box>
        </Box>
      </Box>
    </OpenXAgentPanelSection>
  );
};
